import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import { useAppDispatch } from "../../app/hooks";
import AppButton from "../common/AppButton";
import { editProfile } from "../../features/profile/profileSlice";
import AppForm from "../forms/AppForm";
import { CityList } from "../../utils/constants";
import FileInputAvatar from "../common/FileInputAvatar";
import FormFileSelect from "../forms/FormFileSelect";
import BackIcon from "../IconComp/backIcon";
import { addProfileValidationSchema } from "../forms/validationSchemas";
import { BASE_API_URL } from "../../api";
import { getFileFromUrl, getPharmacyImage } from "../../utils/utils";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ProfileFormInput from "./ProfileFormInput";

interface IProps {
  handleClose: () => void;
  setOpenSnackbar: () => void;
  getProfile: () => void;
  profile: any;
}
const PHARMACY_IMAGE_URL = `${BASE_API_URL}/api/pharmacy/pharmacyProfile/image?pharmacy_id=`;
const ALPHA_NUMERIC_DASH_REGEX = /^[Z0-9-%<>\\$'"*^&#@!!!_+(){}|":?>]+$/;
const EditProfileModal = ({
  handleClose,
  setOpenSnackbar,
  getProfile,
  profile,
}: IProps) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<any>({
    name: "",
    address: "",
    contact_number: "",
    email: "",
    logo: new File([""], "abc.png"),
    //logo: "",
    // user_id: profile?.user?.id,
    // address_id: profile?.pharmacy?.address?.id,
    pharmacy_reg: "",
    license_number: "",
    license_expire_date: "",
    country: "",
    operating_hours: "",
  });

  const [selectedCity, setSelectedCity] = useState({ city: "" });

  useEffect(() => {
    getFileFromUrl(
      PHARMACY_IMAGE_URL + profile?.pharmacy?.id,
      profile?.pharmacy?.logo
    )
      .then((data) => {
        setFormData({
          name: profile?.pharmacy?.name || "",
          address: profile?.pharmacy?.address?.address || "",
          contact_number: profile?.user?.contact_number || "",
          email: profile?.pharmacy?.email || "",
          logo: data || new File([""], "placeholder.png"),
          pharmacy_reg: profile?.user?.pharmacy_reg || "",
          license_number: profile?.user?.license_number || "",
          license_expire_date: profile?.user?.license_expire_date || "",
          country: profile?.pharmacy?.address?.country || "",
          operating_hours: profile?.pharmacy?.operating_hours || "",
        });
        setSelectedCity({ city: profile?.pharmacy?.address?.city || "" }); // Assuming setSelectedCity is a function to update the selected city state
      })
      .catch((err) => {
        setFormData({
          name: profile?.pharmacy?.name || "",
          address: profile?.pharmacy?.address?.address || "",
          contact_number: profile?.user?.contact_number || "",
          email: profile?.pharmacy?.email || "",
          logo: profile?.pharmacy?.logo || "",
          pharmacy_reg: profile?.user?.pharmacy_reg || "",
          license_number: profile?.user?.license_number || "",
          license_expire_date: profile?.user?.license_expire_date || "",
          country: profile?.pharmacy?.address?.country || "",
          operating_hours: profile?.pharmacy?.operating_hours || "",
        });
        setSelectedCity({ city: profile?.pharmacy?.address?.city || "" });
      });
  }, [profile?.pharmacy?.id, profile?.pharmacy?.logo]);

  const handleSubmit = async (values: any) => {
    const pharmacyId = localStorage?.getItem("pharmacyId");
    const userId = profile?.user?.id;
    const addressId = profile?.pharmacy?.address?.id;
    const newFormData: any = new FormData();
    newFormData.append("pharmacy_id", pharmacyId);
    newFormData.append("user_id", userId);
    newFormData.append("address_id", addressId);
    newFormData.append("name", values?.name || "");
    newFormData.append("pharmacy_reg", values?.pharmacy_reg || "");
    newFormData.append("license_number", values?.license_number || "");
    newFormData.append(
      "license_expire_date",
      values?.license_expire_date ? values?.license_expire_date : ""
    );
    newFormData.append("country", values?.country || "");
    newFormData.append(
      "operating_hours",
      values?.operating_hours ? values?.operating_hours : ""
    );
    newFormData.append("city", selectedCity.city || "");
    newFormData.append("address", values?.address || "");
    newFormData.append("contact_number", values?.contact_number || "");

    if (values?.email && values?.email?.length > 0) {
      newFormData.append("email", values?.email);
    }
    // Handle logo based on its type
    if (typeof values?.logo === "string") {
      // If logo is a string (URL), append it directly
      newFormData.append("logo", values?.logo);
    } else {
      // If logo is a File object, append it
      newFormData.append("logo", values?.logo || "");
    }

    try {
      const response = await dispatch(editProfile(newFormData));
      if (response?.payload?.success) {
        setOpenSnackbar();
        handleClose();
        getProfile();
        getPharmacyImage(pharmacyId);
        window.alert("Profile updated successfully!");
      } else {
        window.alert("Failed to update Profile. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      window.alert("Failed to add Profile. Please try again.");
    }
  };

  return (
    <AppForm
      initialValues={formData}
      onSubmit={handleSubmit}
      validationSchema={addProfileValidationSchema}
    >
      <Box>
        <Box sx={{ display: "flex", gap: "20px", width: "100%" }}>
          <Box
            sx={{
              width: "25%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              position: "relative",
              padding: "20px",
            }}
          >
            <Box
              onClick={handleClose}
              sx={{ cursor: "pointer", position: "absolute", top: 0, left: 0 }}
            >
              <BackIcon />
            </Box>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  textAlign: "center",
                  mt: 3,
                }}
              >
                <FileInputAvatar name="logo" />
              </Box>
              <Box sx={{ mt: 2 }}>
                <FormFileSelect name="logo" />
              </Box>
            </Box>
          </Box>
          <Box sx={{ width: "75%" }}>
            <div>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Name
                  </Typography>
                  <ProfileFormInput
                    name="name"
                    as={TextField}
                    fullWidth
                    placeholder="Enter name"
                  />
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Customer Service Number
                  </Typography>
                  <ProfileFormInput
                    name="contact_number"
                    as={TextField}
                    fullWidth
                    placeholder="Customer Service Number"
                  />
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Country
                  </Typography>
                  <ProfileFormInput
                    name="country"
                    as={TextField}
                    fullWidth
                    placeholder="Country"
                  />
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    City
                  </Typography>
                  <Autocomplete
                    disablePortal
                    freeSolo
                    id="combo-box-demo"
                    options={CityList?.map((city) => city?.name)}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: 36,
                        padding: '0px 0px 0px 5px',
                      },
                    }}
                    onChange={(_: any, value: any) => {
                      setSelectedCity({ city: value });
                    }}
                    value={selectedCity?.city}
                    onKeyDown={(event) => {
                      if (ALPHA_NUMERIC_DASH_REGEX.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Box>
                <Box sx={{ width: "100%", gridColumn: "span 2" }}>
                  <Typography variant="h6" gutterBottom>
                    Address
                  </Typography>
                  <ProfileFormInput
                    name="address"
                    as={TextField}
                    fullWidth
                    placeholder="Address"
                  />
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Registration No.
                  </Typography>
                  <ProfileFormInput
                    name="pharmacy_reg"
                    as={TextField}
                    fullWidth
                    placeholder="Pharmacy Reg#"
                  />
                </Box>
                {/* <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Email
                  </Typography>
                  <ProfileFormInput
                    name="email"
                    as={TextField}
                    fullWidth
                    placeholder="Email"
                  />
                </Box> */}
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    License Number
                  </Typography>
                  <ProfileFormInput
                    name="license_number"
                    as={TextField}
                    fullWidth
                    placeholder="License Number#"
                  />
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    License expiry Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={formData?.license_expire_date || ""} // Default to empty string if no value is present
                      onChange={(newValue: any) =>
                        setFormData((prev: any) => ({
                          ...prev,
                          license_expire_date: newValue,
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            sx: {
                              height: 36,
                              "& input": {
                                height: "36px",
                                padding: "0 8px",
                              },
                            },
                          }}
                        />
                      )}
                      inputFormat="dd/MM/yyyy" // Customize the format if needed
                      openTo="day" // Start with the day selection
                      views={["day", "month", "year"]}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Operating Hours
                  </Typography>
                  <ProfileFormInput
                    name="operating_hours"
                    as={TextField}
                    fullWidth
                    placeholder="Operating Hours"
                  />
                </Box>
              </Box>
            </div>
          </Box>
        </Box>
        <Box sx={{ width: "100%", mt: 2, float: "right" }}>
          <AppButton
            title="Update"
            style={{ backgroundColor: "#01A768", float: "right" }}
            type="submit"
          />
        </Box>
      </Box>
    </AppForm>
  );
};
export default EditProfileModal;
